import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
// SERVICES
import {
  createWorkShift,
  deleteWorkShift,
  editWorkShift,
  getWorkShifts,
} from "../../../api/workShift";
// APP
import RemoveModal from "../../modals/removeModal";
// MATERIAL UI
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import { alpha, useTheme } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// ICONS
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WorkIcon from "@mui/icons-material/Work";

import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import CreateOrEditWorkShiftModal from "./CreateOrEditWorkShiftModal";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: {
      duration: 0.2,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0px 5px 10px rgba(0, 0, 0, 0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

const TimeDisplay = ({ label, time }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
      <AccessTimeIcon
        fontSize="small"
        sx={{ color: theme.palette.text.secondary, mr: 0.5 }}
      />
      <Typography variant="body2" color="textSecondary" sx={{ mr: 0.5 }}>
        {label}:
      </Typography>
      <Typography variant="body2" fontWeight="medium">
        {time}h
      </Typography>
    </Box>
  );
};

const WorkShiftsList = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [workShifts, setWorkShifts] = useState([]);
  const [editingWorkShift, setEditingWorkShift] = useState(null);
  const [showCreateOrEditWorkShiftModal, setShowCreateOrEditWorkShiftModal] =
    useState(false);

  const [workShiftObjToDelete, setWorkShiftObjToDelete] = useState(null);
  const [showWorkShiftDeleteModal, setShowWorkShiftDeleteModal] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setTimeout(async () => {
      await loadWorkShifts();
    }, 10);
  }, []);

  const loadWorkShifts = async () => {
    setLoading(true);
    try {
      const res = await getWorkShifts();
      setWorkShifts(res.data);
    } catch (error) {
      setErrorMessage(
        "Não foi possível carregar os turnos de trabalho. Tente novamente mais tarde."
      );
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEditModal = () => {
    setShowCreateOrEditWorkShiftModal(!showCreateOrEditWorkShiftModal);
    setEditingWorkShift(null);
  };

  const handleCreateWorkShift = async (workShiftObj) => {
    try {
      await createWorkShift(workShiftObj);
      handleCreateEditModal();
      await loadWorkShifts();
      dispatchSnackBarMessage(
        `create-work-shift-success-${new Date()}`,
        "Turno cadastrado com sucesso!",
        "success"
      );
    } catch (e) {
      const errorData = e.response?.data || {};
      let messages = "";
      for (const prop in errorData) {
        if (errorData.hasOwnProperty(prop)) {
          messages += errorData[prop].join("\n") + "\n";
        }
      }
      dispatchSnackBarMessage(
        `create-work-shift-${new Date()}`,
        messages || "Erro ao cadastrar turno",
        "error"
      );
    }
  };

  const handleEditWorkShift = (workShift) => {
    setEditingWorkShift(workShift);
    setShowCreateOrEditWorkShiftModal(!showCreateOrEditWorkShiftModal);
  };

  const confirmEditWorkShift = async (workShiftObj) => {
    try {
      await editWorkShift(workShiftObj, workShiftObj.id);
      handleCreateEditModal();
      setEditingWorkShift(null);
      await loadWorkShifts();
      dispatchSnackBarMessage(
        `edit-work-shift-success-${new Date()}`,
        "Turno atualizado com sucesso!",
        "success"
      );
    } catch (e) {
      const errorData = e.response?.data || {};
      let messages = "";
      for (const prop in errorData) {
        if (errorData.hasOwnProperty(prop)) {
          messages += errorData[prop].join("\n") + "\n";
        }
      }
      dispatchSnackBarMessage(
        `edit-work-shift-${new Date()}`,
        messages || "Erro ao editar turno",
        "error"
      );
    }
  };

  const handleDeleteWorkShiftModal = (workShift) => {
    setWorkShiftObjToDelete(workShift);
    setShowWorkShiftDeleteModal(!showWorkShiftDeleteModal);
  };

  const handleDeleteWorkShift = async () => {
    try {
      await deleteWorkShift(workShiftObjToDelete.id);
      setShowWorkShiftDeleteModal(false);
      setWorkShiftObjToDelete(null);
      await loadWorkShifts();
      dispatchSnackBarMessage(
        `delete-work-shift-success-${new Date()}`,
        "Turno removido com sucesso!",
        "success"
      );
    } catch (error) {
      dispatchSnackBarMessage(
        `delete-work-shift-error-${new Date()}`,
        "Erro ao remover turno",
        "error"
      );
      setShowWorkShiftDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Box sx={{ p: 3 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={50}
              sx={{ mb: 2, borderRadius: 2 }}
            />
            {[1, 2, 3].map((item) => (
              <Skeleton
                key={item}
                variant="rectangular"
                width="100%"
                height={80}
                sx={{ mb: 2, borderRadius: 2 }}
              />
            ))}
          </Box>
        </motion.div>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Collapse in={showError} sx={{ mb: 2 }}>
          <Alert
            severity="error"
            onClose={() => setShowError(false)}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        </Collapse>

        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                variant="contained"
                startIcon={<AddCircleIcon />}
                onClick={handleCreateEditModal}
                color="primary"
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: "medium",
                  boxShadow: theme.shadows[3],
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                }}
              >
                Cadastrar Novo Turno
              </Button>
            </motion.div>
          </Box>
        </motion.div>

        <Box
          sx={{
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            borderRadius: 3,
            p: 2,
            boxShadow: theme.shadows[1],
            backdropFilter: "blur(8px)",
          }}
        >
          {workShifts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  px: 2,
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                  borderStyle: "dashed",
                  borderWidth: 1,
                  borderColor: theme.palette.divider,
                }}
              >
                <ScheduleIcon
                  sx={{
                    fontSize: 48,
                    color: theme.palette.action.disabled,
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Nenhum turno cadastrado
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                  sx={{ mb: 2, maxWidth: 400 }}
                >
                  Cadastre um novo turno para organizar os horários de trabalho
                  da sua equipe.
                </Typography>
                <motion.div
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleIcon />}
                    onClick={handleCreateEditModal}
                    sx={{ mt: 1, borderRadius: 2 }}
                  >
                    Adicionar Turno
                  </Button>
                </motion.div>
              </Box>
            </motion.div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <AnimatePresence>
                {workShifts.map((workShiftItem) => (
                  <motion.div
                    key={workShiftItem.id}
                    variants={itemVariants}
                    exit="exit"
                    layout
                  >
                    <Card
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        overflow: "visible",
                        boxShadow: theme.shadows[2],
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: theme.shadows[5],
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          sx={{
                            p: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: { xs: 1, sm: 0 },
                              flexGrow: 1,
                            }}
                          >
                            <Box
                              sx={{
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1
                                ),
                                borderRadius: "50%",
                                p: 1,
                                mr: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <WorkIcon color="primary" />
                            </Box>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: "medium",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: { xs: "20ch", sm: "30ch" },
                                }}
                              >
                                {workShiftItem.name}
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  mt: 0.5,
                                }}
                              >
                                <TimeDisplay
                                  label="Início"
                                  time={workShiftItem.start_time}
                                />
                                <TimeDisplay
                                  label="Fim"
                                  time={workShiftItem.end_time}
                                />
                              </Box>
                            </Box>
                          </Box>

                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                              ml: { xs: 0, sm: 2 },
                              alignSelf: { xs: "flex-end", sm: "center" },
                            }}
                          >
                            <Tooltip title="Editar turno">
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <IconButton
                                  size="small"
                                  aria-label="edit"
                                  color="primary"
                                  onClick={() =>
                                    handleEditWorkShift(workShiftItem)
                                  }
                                  sx={{
                                    backgroundColor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    "&:hover": {
                                      backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.2
                                      ),
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </motion.div>
                            </Tooltip>
                            <Tooltip title="Excluir turno">
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <IconButton
                                  size="small"
                                  aria-label="delete"
                                  color="error"
                                  onClick={() =>
                                    handleDeleteWorkShiftModal(workShiftItem)
                                  }
                                  sx={{
                                    backgroundColor: alpha(
                                      theme.palette.error.main,
                                      0.1
                                    ),
                                    "&:hover": {
                                      backgroundColor: alpha(
                                        theme.palette.error.main,
                                        0.2
                                      ),
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </motion.div>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </Box>
      </Box>

      <RemoveModal
        title="Excluir turno de trabalho"
        message={`Tem certeza que deseja excluir o turno "${workShiftObjToDelete?.name}" (${workShiftObjToDelete?.start_time}h - ${workShiftObjToDelete?.end_time}h)?`}
        show={showWorkShiftDeleteModal}
        toggleModal={handleDeleteWorkShiftModal}
        onRemove={handleDeleteWorkShift}
      />

      {showCreateOrEditWorkShiftModal && (
        <CreateOrEditWorkShiftModal
          show={showCreateOrEditWorkShiftModal}
          closeModal={handleCreateEditModal}
          createWorkShift={handleCreateWorkShift}
          editWorkShift={confirmEditWorkShift}
          workShift={editingWorkShift}
        />
      )}
    </>
  );
};

export default WorkShiftsList;
